import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import API_BASE_URL from '../config/api';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';
import './Facilities.css';
import { AuthContext } from '../context/AuthContext';

const Facilities = () => {
  const { hasPermission } = useContext(AuthContext);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [togglingStatus, setTogglingStatus] = useState({});
  const navigate = useNavigate();

  const categories = [
    { value: 'general', label: 'General' },
    { value: 'room', label: 'Room' },
    { value: 'dining', label: 'Dining' },
    { value: 'recreation', label: 'Recreation' },
    { value: 'business', label: 'Business' },
    { value: 'transport', label: 'Transport' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    fetchFacilities();
  }, []);

  const fetchFacilities = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/facilities`);
      setFacilities(response.data.data || []);
      setLoading(false);
    } catch (error) {
      setError('Failed to fetch facilities');
      setLoading(false);
    }
  };


  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this facility?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/admin/facilities/${id}`);
      fetchFacilities();
    } catch (error) {
      setError('Failed to delete facility');
    }
  };

  const handleToggleActive = async (facilityId, currentStatus) => {
    const newStatus = !currentStatus;
    setTogglingStatus(prev => ({ ...prev, [facilityId]: true }));
    
    try {
      const response = await axios.put(`${API_BASE_URL}/admin/facilities/${facilityId}`, {
        isActive: newStatus
      });
      
      // Update the facility in the local state
      setFacilities(facilities.map(facility => 
        facility._id === facilityId 
          ? { ...facility, isActive: response.data.data.isActive }
          : facility
      ));
    } catch (error) {
      setError('Failed to update facility status');
    } finally {
      setTogglingStatus(prev => ({ ...prev, [facilityId]: false }));
    }
  };

  const pagination = usePagination(facilities, { noun: 'facilities' });

  if (loading) {
    return (
      <Layout>
        <div className="container">
          <div className="facilities-header">
            <h1>Facilities Management</h1>
            {hasPermission('facilities.create') && (
              <Link to="/sy-admin/facilities/new" className="btn btn-primary">
                <span className="btn-icon">+</span>
                Add New Facility
              </Link>
            )}
          </div>
          <div className="facilities-table-container">
            <table className="facilities-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Order</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((index) => (
                  <tr key={index} className="skeleton-row">
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '60%', height: '16px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '80px', height: '24px', borderRadius: '12px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '40px', height: '14px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-switch" style={{ width: '100px', height: '26px' }}></div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <div className="skeleton skeleton-button" style={{ width: '36px', height: '36px', borderRadius: '8px' }}></div>
                        <div className="skeleton skeleton-button" style={{ width: '36px', height: '36px', borderRadius: '8px' }}></div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container">
        <div className="facilities-header">
          <h1>Facilities Management</h1>
          {hasPermission('facilities.create') && (
            <Link to="/sy-admin/facilities/new" className="btn btn-primary">
              <span className="btn-icon">+</span>
              Add New Facility
            </Link>
          )}
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="facilities-stats">
          <div className="stat-card">
            <h3>{facilities.length}</h3>
            <p>Total Facilities</p>
          </div>
          <div className="stat-card">
            <h3>{facilities.filter(f => f.isActive).length}</h3>
            <p>Active Facilities</p>
          </div>
        </div>

        {facilities.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🏢</div>
            <h2>No facilities found</h2>
            <p>Get started by adding your first facility to the platform.</p>
            {hasPermission('facilities.create') && (
              <Link to="/sy-admin/facilities/new" className="btn btn-primary">
                Add Your First Facility
              </Link>
            )}
          </div>
        ) : (
          <div className="facilities-table-container">
            <table className="facilities-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Order</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagination.items.map((facility) => {
                  const isTogglingStatus = togglingStatus[facility._id];
                  return (
                    <tr key={facility._id}>
                      <td>
                        <div className="facility-name-cell">
                          <span className="facility-name-text">
                            {facility.icon && <span className="facility-icon-inline">{facility.icon}</span>}
                            {facility.name}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="facility-category-cell">
                          <span className="category-badge">
                            {categories.find(c => c.value === facility.category)?.label || facility.category}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="facility-order-cell">
                          <span>{facility.displayOrder}</span>
                        </div>
                      </td>
                      <td>
                        <div className="status-cell">
                          <label className="status-toggle-label">
                            <div className="toggle-switch">
                              <input
                                type="checkbox"
                                checked={facility.isActive}
                                onChange={() => handleToggleActive(facility._id, facility.isActive)}
                                disabled={isTogglingStatus}
                                className="toggle-input"
                              />
                              <span className={`toggle-slider ${isTogglingStatus ? 'loading' : ''}`}></span>
                            </div>
                            <span className={`status-text ${facility.isActive ? 'status-active-text' : 'status-inactive-text'}`}>
                              {facility.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </label>
                        </div>
                      </td>
                      <td>
                        <div className="table-actions">
                          {hasPermission('facilities.update') && (
                            <button
                              onClick={() => navigate(`/sy-admin/facilities/edit/${facility._id}`)}
                              className="btn btn-edit btn-icon-only"
                              title="Edit Facility"
                            >
                              <span className="btn-icon">✏️</span>
                            </button>
                          )}
                          {hasPermission('facilities.delete') && (
                            <button
                              onClick={() => handleDelete(facility._id)}
                              className="btn btn-delete btn-icon-only"
                              title="Delete Facility"
                            >
                              <span className="btn-icon">🗑️</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Pagination {...pagination.props} />
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Facilities;

